<script setup>
import { ref, computed, provide, watch, onMounted, onUnmounted } from 'vue';
import Design02Trigger from './Design02Trigger.vue';
import Design02Drawer from './Design02Drawer.vue';
import Toaster from '../../components/ui/Toaster.vue';
import { useAgent } from '../../composables/useAgent.js';
import { useToast } from '../../composables/useToast.js';
import { useTranslations } from '../../composables/useTranslations.js';
import { useScrollLock } from '../../composables/useScrollLock.js';

const props = defineProps({
    config: { type: Object, required: true },
    events: { type: Object, required: true },
});

provide('aia.config', props.config);
provide('aia.events', props.events);

const open = ref(Boolean(props.config?.openOnLoad));

const { messages, status, isBusy, canRetry, isBlocked, send, retryLast } = useAgent(props.config, props.events);

// Freeze the background page while the drawer is open on mobile (where it
// covers the full screen). Prevents scroll bleed-through and the horizontal
// rubber-band pan, and restores the page's scroll position on close.
useScrollLock(open);

const toast = useToast();
const { t } = useTranslations(props.config?.locale);

// Trigger badge state while the drawer is collapsed: 'error' | 'success' | null.
// Cleared when the drawer is reopened.
const notice = ref(null);

// React to terminal turn outcomes. Errors always raise a toast (important even
// when the drawer is open); a completed reply only toasts when collapsed (when
// open, the user already sees it streaming in). The badge is set only while
// collapsed so it can pull the user back.
watch(status, (next, prev) => {
    if (prev === 'streaming' && next === 'idle') {
        if (!open.value) {
            notice.value = 'success';
            toast.success(t('toast.replyReady'));
        }
    } else if (['error', 'offline', 'rate-limited'].includes(next)) {
        toast.error(t(`states.${next === 'rate-limited' ? 'rateLimited' : next}`));
        if (!open.value) notice.value = 'error';
    }
});

function toggle() {
    open.value ? close() : openDrawer();
}
function openDrawer() {
    if (open.value) return;
    open.value = true;
    notice.value = null;
    props.events?.emit?.('open');
}
function close() {
    if (!open.value) return;
    open.value = false;
    props.events?.emit?.('close');
}

function handleSend(text) {
    openDrawer();
    send(text);
}

function handleEscape(e) {
    if (e.key === 'Escape' && open.value) close();
}

onMounted(() => document.addEventListener('keydown', handleEscape));
onUnmounted(() => document.removeEventListener('keydown', handleEscape));

// External AIWidget.open() / close() hooks.
props.events?.on?.('request-open', openDrawer);
props.events?.on?.('request-close', close);

// Confirm to the visitor when the assistant successfully files their details.
props.events?.on?.('lead-created', () => {
    toast.success(t('toast.leadSent'));
    if (!open.value) notice.value = 'success';
});

// `100%` (not `100vw`): for a fixed element this resolves to the visual
// viewport width, so on mobile the drawer is exactly full width with no
// horizontal overflow/drift. `100vw` would overshoot by the scrollbar width.
const drawerWidth = 'min(420px, 100%)';
</script>

<template>
    <Design02Drawer
        :open="open"
        :messages="messages"
        :status="status"
        :is-busy="isBusy"
        :can-retry="canRetry"
        :is-blocked="isBlocked"
        @close="close"
        @send="handleSend"
        @send-prompt="handleSend"
        @retry="retryLast"
    />
    <Design02Trigger
        :open="open"
        :drawer-width="drawerWidth"
        :notice="notice"
        @toggle="toggle"
    />
    <Toaster />
</template>
